import { FormControl, FormHelperText, IconButton, TextFieldPropsSizeOverrides, Typography } from '@mui/material';
import { OverridableStringUnion } from '@mui/types';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs'; // Import dayjs for date formatting
import React, { useEffect, useState } from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import { IDateFormat } from '../../commonFunctions/CommonFunctions';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { InputAdornment } from '@mui/material';

interface ControlledDateFieldProps {
  label: string;
  id: string;
  name: string;
  className?: string;
  variant?: 'standard' | 'filled' | 'outlined';
  disabled?: boolean;
  control: any;
  defaultValue?: any;
  rules: UseControllerProps['rules'];
  onChange?: (date: Date | null) => void;
  required?: boolean;
  setValue?: any;
  dateFormat?: IDateFormat;
  denyFuturOrPast?: 'none' | 'future' | 'past' | 'custom';
  description?: IDescription;
  labelType?: "Material" | "Normal";
  dateSize?: OverridableStringUnion<'small' | 'medium', TextFieldPropsSizeOverrides>;
  isInGridFilter?: boolean
}

interface IDescription {
  text: string | any;
  align: "Top" | "Bottom";
  style?: React.CSSProperties;
  className?: string;
}

const WSDateField: React.FC<ControlledDateFieldProps> = ({
  id,
  label,
  className,
  control,
  name,
  rules,
  variant = "outlined",
  disabled = false,
  defaultValue = null,
  setValue,
  onChange,
  dateFormat = "MM/DD/YYYY",
  denyFuturOrPast,
  labelType = "Material",
  description,
  dateSize = "small",
  isInGridFilter = false,
}) => {

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (setValue && defaultValue) {
      setValue(name, dayjs(defaultValue));
    }
  }, [setValue, name, defaultValue]);

  const maxDate =
    denyFuturOrPast === "future" ? dayjs() : undefined;

  const minDate =
    denyFuturOrPast === "past"
      ? dayjs()
      : denyFuturOrPast === "custom"
        ? dayjs(defaultValue)
        : undefined;

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue ? dayjs(defaultValue) : null}
      rules={rules}
      render={({ field, fieldState }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <FormControl
            className={className}
            disabled={disabled}
            error={!!fieldState.error}
            fullWidth
          >

            {description && description.align === "Top" && (
              <Typography style={description.style}>
                {description.text}
              </Typography>
            )}

            <DatePicker
              label={label}
              value={
                field.value
                  ? dayjs.isDayjs(field.value)
                    ? field.value
                    : dayjs(field.value)
                  : null
              }
              format={dateFormat}
              disabled={disabled}
              maxDate={maxDate}
              minDate={minDate}
              onChange={(date) => {
                field.onChange(date);
                if (onChange) onChange(date ? date.toDate() : null);
              }}
              slotProps={{
                textField: {
                  size: dateSize,
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                  variant: variant,
                },
              }}
            />

            {description && description.align === "Bottom" && (
              <Typography
                style={description.style}
                className={description?.className}
              >
                {description.text}
              </Typography>
            )}

          </FormControl>
        </LocalizationProvider>
      )}
    />
  );
};

export default WSDateField;
